import { format, formatAge, formatInstant, fromColumn, mirrorAge } from '@evogard/domain';

/**
 * Os dois valores que atravessam módulo: dinheiro e "quando foi".
 *
 * ## Por que eles moram aqui
 *
 * Estavam duplicados dentro dos módulos: `MoneyValue` idêntico, caractere por
 * caractere, em `billing/common.tsx` e `sales/common.tsx`; `Age` idêntico em
 * `billing/common.tsx` e `erp/common.tsx`. Não foi descuido — o
 * `boundaries.test.ts` derruba o import do vizinho, com razão, e a saída fácil
 * de "preciso do que o outro módulo tem" é copiar.
 *
 * A regra do `CLAUDE.md` diz o que fazer no lugar disso: *o que dois módulos
 * precisam não é de nenhum dos dois*. Regra pura vai para `@evogard/domain` —
 * e é de lá que vêm `fromColumn`, `format`, `mirrorAge` e `formatAge`. O que
 * sobra é a **apresentação** dessas regras, que é de tela e por isso mora
 * aqui, ao lado de `Table`.
 *
 * Não subiram para `@evogard/ui` porque o pacote não depende de
 * `@evogard/domain` — dar-lhe essa dependência é mudança de camada, e é a
 * mesma que a pendência de `TEMAS` no `DESIGN.md` espera. Quando ela
 * acontecer, estes dois vão junto e o portal para de ter o `asMoney` dele.
 */

/**
 * Dinheiro, sempre pela coluna `numeric(12,2)`.
 *
 * `Number(linha.amount)` funciona em todo teste e perde o centavo na
 * conciliação do mês — por isso a conversão é `fromColumn`, que devolve
 * centavo inteiro, e a formatação é `format`.
 *
 * **Valor ilegível diz que é ilegível.** Nenhuma tela afirma o que o código não
 * cumpre, e um `R$ 0,00` no lugar de um número que não coube é a pior forma
 * disso: parece precisão.
 */
export function MoneyValue({ value }: { value: string | null }) {
  if (value === null) return <span className="text-texto-suave">—</span>;
  try {
    const d = fromColumn(value);
    if (d === null) return <span className="text-texto-suave">—</span>;
    return <span className="tabular-nums">{format(d)}</span>;
  } catch {
    return <span className="font-medium text-erro">Valor ilegível ({value})</span>;
  }
}

/**
 * Quando foi, dito das duas formas.
 *
 * O relativo é o que se lê de relance; o absoluto é o que se copia para um
 * chamado. Não há limiar de "velho" escrito aqui: a cadência da coleta mora no
 * servidor, e uma segunda cópia dela nesta tela ficaria para trás no dia em que
 * alguém mudasse o cron — dizendo "está em dia" sobre uma coleta que não roda
 * há uma semana. Então a tela mostra sempre a idade, e quem lê decide.
 *
 * Não se atualiza sozinho: é calculado na renderização. Um relógio correndo
 * dentro de um cartão é movimento que não comunica estado nenhum.
 */
export function Age({ iso }: { iso: string }) {
  const ms = mirrorAge(new Date(iso), new Date());
  return (
    <span className="whitespace-nowrap">
      {ms === null ? '—' : formatAge(ms)}
      <span className="text-texto-suave"> · {formatInstant(iso)}</span>
    </span>
  );
}
